import React, { useState } from 'react';
import CustomError from './CustomError';

/* Validation */
import { Formik } from 'formik';
import { validationSchema } from './formConfig';

/* Gjensidige Core 20 Imports */
import utils from '@gjensidige/core-utilities-css';

/* Gjensidige Core 30 Imports */
import { Alert } from '@gjensidige/core-components/lib/alerts/default';
import { Col, Row } from '@gjensidige/nci-core-grid';
import { Input } from '@gjensidige/core-components/lib/forms/input';
import { TextArea } from '@gjensidige/core-components/lib/forms/textarea';
import { Text, Title } from '@gjensidige/nci-core-typography';
import { InputGroupSimple } from '@gjensidige/core-components/lib/forms/input-group-simple';
import { Checkbox } from '@gjensidige/core-components/lib/forms/checkbox';
import { Datepicker } from '@gjensidige/core-components/lib/datepicker';

/* API */
import axios from 'axios';
import { Endpoints } from '@internal/common';
import {Button} from "@gjensidige/core-components/lib/button";

export interface IProps {
  claimType: string;
}

const ClaimReportGlasses: React.FC<IProps> = (props) => {
  const [formData, setFormData] = useState({
    claimType: props.claimType,
    cvrNumber: '',
    policyNumber: '',
    contactPersonName: '',
    email: '',
    contactPersonPhoneNumber: '',
    injuredPartyName: '',
    injuredPartyDOB: '',
    injuredPartyEmployment: '',
    injuredPartyAddress: '',
    isInjuredPartyEmployedAtReporter: '',
    injuryDate: '',
    injuryLocation: '',
    kindOfWorkWhenInjured: '',
    injuryReason: '',
    hadGlassesOn: '',
    glassesLocation: '',
    dateOfPurchase: '',
    originalPriceOfGlasses: '',
    priceEstimate: '',
    consent: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState(false);

  const sendFormData = () => {
    setApiError(false);
    axios
      .post(Endpoints.ClaimReportDIByggeri, formData)
      .then(() => setSubmitted(true))
      .catch(() => setApiError(true));
  };

  const dateOfBirthDateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const target = event.target;
    const value = target.value;
    setFormData({ ...formData, injuredPartyDOB: value });
  };

  const dateofPurchaseChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    const value = target.value;
    setFormData({ ...formData, dateOfPurchase: value });
  };

  const todaysDate = new Date(new Date().setHours(0, 0, 0, 0));
  return (
    <>
      {submitted ? (
        <Alert
          alertType="success"
          showIcon
          title="Tak for din anmeldelse​"
          text="Vi sender dig en kvitteringsmail med informationerne fra din skadeanmeldelse.​"
          className={utils['mb-3']}
        />
      ) : (
        <>
          <div className={utils['pb-2']}>
            <Title tag="h5">Anmeld skade på briller</Title>
            <Text size="small">* Påkrævede felter</Text>
          </div>
          <Formik
            enableReinitialize
            initialValues={{
              cvrNumber: formData.cvrNumber,
              policyNumber: formData.policyNumber,
              contactPersonName: formData.contactPersonName,
              email: formData.email,
              contactPersonPhoneNumber: formData.contactPersonPhoneNumber,
              injuredPartyName: formData.injuredPartyName,
              injuredPartyDOB: formData.injuredPartyDOB,
              injuredPartyEmployment: formData.injuredPartyEmployment,
              injuredPartyAddress: formData.injuredPartyAddress,
              isInjuredPartyEmployedAtReporter:
                formData.isInjuredPartyEmployedAtReporter,
              injuryDate: formData.injuryDate,
              injuryLocation: formData.injuryLocation,
              kindOfWorkWhenInjured: formData.kindOfWorkWhenInjured,
              injuryReason: formData.injuryReason,
              hadGlassesOn: formData.hadGlassesOn,
              glassesLocation: formData.glassesLocation,
              dateOfPurchase: formData.dateOfPurchase,
              originalPriceOfGlasses: formData.originalPriceOfGlasses,
              priceEstimate: formData.priceEstimate,
              consent: formData.consent
            }}
            validationSchema={validationSchema(formData)}
            onSubmit={() => {
              sendFormData();
            }}
          >
            {(props) => {
              const {
                handleSubmit,
                handleBlur,
                setFieldValue,
                touched,
                errors
              } = props;
              return (
                <form onSubmit={handleSubmit} className={utils['pb-3']}>
                  <Row>
                    <Col lg={12}>
                      <Input
                        id="cvr-number"
                        labelText="CVR-nummer *"
                        name="cvrNumber"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('cvrNumber', event.target.value);
                          setFormData({
                            ...formData,
                            cvrNumber: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={!!(touched.cvrNumber && errors.cvrNumber)}
                        errorText={errors.cvrNumber}
                        value={formData.cvrNumber}
                      />
                      <Input
                        id="policy-number"
                        labelText="Policenummer *"
                        name="policyNumber"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('policyNumber', event.target.value);
                          setFormData({
                            ...formData,
                            policyNumber: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(touched.policyNumber && errors.policyNumber)
                        }
                        errorText={errors.policyNumber}
                        value={formData.policyNumber}
                      />
                      <Input
                        id="contactPersonName"
                        labelText="Navn (kontaktperson) *"
                        name="contactPersonName"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue(
                            'contactPersonName',
                            event.target.value
                          );
                          setFormData({
                            ...formData,
                            contactPersonName: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(
                            touched.contactPersonName &&
                            errors.contactPersonName
                          )
                        }
                        errorText={errors.contactPersonName}
                        value={formData.contactPersonName}
                      />
                      <Input
                        id="email"
                        labelText="Email (kontaktperson) *"
                        name="email"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('email', event.target.value);
                          setFormData({
                            ...formData,
                            email: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={!!(touched.email && errors.email)}
                        errorText={errors.email}
                        value={formData.email}
                      />
                      <Input
                        id="contactPersonPhoneNumber"
                        labelText="Telefonnummer (kontaktperson) *"
                        name="contactPersonPhoneNumber"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue(
                            'contactPersonPhoneNumber',
                            event.target.value
                          );
                          setFormData({
                            ...formData,
                            contactPersonPhoneNumber: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(
                            touched.contactPersonPhoneNumber &&
                            errors.contactPersonPhoneNumber
                          )
                        }
                        errorText={errors.contactPersonPhoneNumber}
                        value={formData.contactPersonPhoneNumber}
                      />
                      <div className={utils['pb-2']}>
                        <Title tag="h5">Om skaden</Title>
                      </div>
                      <Input
                        id="injuredPartyName"
                        labelText="Skadelidtes fulde navn *"
                        name="injuredPartyName"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('injuredPartyName', event.target.value);
                          setFormData({
                            ...formData,
                            injuredPartyName: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(
                            touched.injuredPartyName && errors.injuredPartyName
                          )
                        }
                        errorText={errors.injuredPartyName}
                        value={formData.injuredPartyName}
                      />
                      <Datepicker
                        labelText="Fødselsdato *"
                        name="injuredPartyDOB"
                        id="injuredPartyDOB"
                        className={utils['pb-2']}
                        onChange={(event, value) => {
                          dateOfBirthDateChange(event);
                          setFieldValue('injuredPartyDOB', value);
                        }}
                        invalid={
                          !!(touched.injuredPartyDOB && errors.injuredPartyDOB)
                        }
                        errorText={errors.injuredPartyDOB}
                        dateFormat={{
                          format: 'dd/MM/yyyy',
                          parseFormula: {
                            groups: { date: 1, month: 2, year: 3 },
                            regExp: /^(\d{1,2}).(\d{1,2}).(\d{4})$/
                          },
                          placeholder: 'dd/MM/yyyy'
                        }}
                        placeholder="DD/MM/YYYY"
                        value={formData.injuredPartyDOB}
                      />
                      <Input
                        id="injuredPartyEmployment"
                        labelText="Skadelidtes stilling eller beskæftigelse *"
                        name="injuredPartyEmployment"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue(
                            'injuredPartyEmployment',
                            event.target.value
                          );
                          setFormData({
                            ...formData,
                            injuredPartyEmployment: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(
                            touched.injuredPartyEmployment &&
                            errors.injuredPartyEmployment
                          )
                        }
                        errorText={errors.injuredPartyEmployment}
                        value={formData.injuredPartyEmployment}
                      />
                      <TextArea
                        id="injuredPartyAddress"
                        labelText="Skadelidtes adresse *"
                        maxLength={500}
                        name="injuredPartyAddress"
                        className={utils['pb-2']}
                        placeholder="Oplys vejnavn og nummer, postnummer samt by."
                        value={formData.injuredPartyAddress}
                        onChange={(
                          event: React.ChangeEvent<HTMLTextAreaElement>
                        ) => {
                          setFieldValue(
                            'injuredPartyAddress',
                            event.target.value
                          );
                          setFormData({
                            ...formData,
                            injuredPartyAddress: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(
                            touched.injuredPartyAddress &&
                            errors.injuredPartyAddress
                          )
                        }
                        errorText={errors.injuredPartyAddress}
                      />
                      <InputGroupSimple
                        vertical
                        header="Er skadelidte ansat hos anmelderen? *"
                        id="isInjuredPartyEmployedAtReporter"
                        className={utils['pb-2']}
                        name="isInjuredPartyEmployedAtReporter"
                        centered={false}
                        onChange={(event) => {
                          setFormData({
                            ...formData,
                            isInjuredPartyEmployedAtReporter: event.target.value
                          });
                          setFieldValue(
                            'isInjuredPartyEmployedAtReporter',
                            event.target.value
                          );
                        }}
                        options={[
                          {
                            label: 'Ja',
                            value: 'Ja'
                          },
                          {
                            label: 'Nej',
                            value: 'Nej'
                          }
                        ]}
                        type="radio"
                        value={formData.isInjuredPartyEmployedAtReporter}
                        onBlur={handleBlur}
                        invalid={
                          !!(
                            touched.isInjuredPartyEmployedAtReporter &&
                            errors.isInjuredPartyEmployedAtReporter
                          )
                        }
                        errorText={errors.isInjuredPartyEmployedAtReporter}
                      />
                      <Input
                        id="injuryDate"
                        labelText="Hvornår skete skaden? *"
                        helpText="Skriv gerne dato og klokkeslæt."
                        helpTextOpen
                        name="injuryDate"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('injuryDate', event.target.value);
                          setFormData({
                            ...formData,
                            injuryDate: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={!!(touched.injuryDate && errors.injuryDate)}
                        errorText={errors.injuryDate}
                        value={formData.injuryDate}
                      />
                      <Input
                        id="injuryLocation"
                        labelText="Hvor skete skaden? *"
                        name="injuryLocation"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('injuryLocation', event.target.value);
                          setFormData({
                            ...formData,
                            injuryLocation: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(touched.injuryLocation && errors.injuryLocation)
                        }
                        errorText={errors.injuryLocation}
                        value={formData.injuryLocation}
                      />
                      <TextArea
                        id="kindOfWorkWhenInjured"
                        labelText="Hvilket arbejde var skadelidte beskæftiget med, da skaden skete?  *"
                        maxLength={500}
                        name="kindOfWorkWhenInjured"
                        className={utils['pb-2']}
                        value={formData.kindOfWorkWhenInjured}
                        onChange={(
                          event: React.ChangeEvent<HTMLTextAreaElement>
                        ) => {
                          setFieldValue(
                            'kindOfWorkWhenInjured',
                            event.target.value
                          );
                          setFormData({
                            ...formData,
                            kindOfWorkWhenInjured: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(
                            touched.kindOfWorkWhenInjured &&
                            errors.kindOfWorkWhenInjured
                          )
                        }
                        errorText={errors.kindOfWorkWhenInjured}
                      />
                      <TextArea
                        id="injuryReason"
                        labelText="Hvad var årsagen til at brillen blev beskadiget?  *"
                        maxLength={500}
                        name="injuryReason"
                        className={utils['pb-2']}
                        value={formData.injuryReason}
                        onChange={(
                          event: React.ChangeEvent<HTMLTextAreaElement>
                        ) => {
                          setFieldValue('injuryReason', event.target.value);
                          setFormData({
                            ...formData,
                            injuryReason: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(touched.injuryReason && errors.injuryReason)
                        }
                        errorText={errors.injuryReason}
                      />
                      <InputGroupSimple
                        vertical
                        header="Havde skadelidte brillen på, da skaden skete? *"
                        id="hadGlassesOn"
                        className={utils['pb-2']}
                        name="hadGlassesOn"
                        centered={false}
                        onChange={(event) => {
                          setFormData({
                            ...formData,
                            hadGlassesOn: event.target.value
                          });
                          setFieldValue('hadGlassesOn', event.target.value);
                        }}
                        options={[
                          {
                            label: 'Ja',
                            value: 'Ja'
                          },
                          {
                            label: 'Nej',
                            value: 'Nej'
                          }
                        ]}
                        type="radio"
                        value={formData.hadGlassesOn}
                        onBlur={handleBlur}
                        invalid={
                          !!(touched.hadGlassesOn && errors.hadGlassesOn)
                        }
                        errorText={errors.hadGlassesOn}
                      />
                      {formData.hadGlassesOn === 'Nej' && (
                        <Input
                          id="glassesLocation"
                          labelText="Hvor var brillen henne? *"
                          name="glassesLocation"
                          className={utils['pb-2']}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            setFieldValue(
                              'glassesLocation',
                              event.target.value
                            );
                            setFormData({
                              ...formData,
                              glassesLocation: event.target.value
                            });
                          }}
                          onBlur={handleBlur}
                          invalid={
                            !!(
                              touched.glassesLocation && errors.glassesLocation
                            )
                          }
                          errorText={errors.glassesLocation}
                          value={formData.glassesLocation}
                        />
                      )}
                      <Datepicker
                        labelText="Hvornår blev den beskadigede brille købt? *"
                        name="dateOfPurchase"
                        id="dateOfPurchase"
                        className={utils['pb-2']}
                        onChange={(event, value) => {
                          dateofPurchaseChange(event);
                          setFieldValue('dateOfPurchase', value);
                        }}
                        invalid={
                          !!(touched.dateOfPurchase && errors.dateOfPurchase)
                        }
                        maxDate={todaysDate}
                        errorText={errors.dateOfPurchase}
                        dateFormat={{
                          format: 'dd/MM/yyyy',
                          parseFormula: {
                            groups: { date: 1, month: 2, year: 3 },
                            regExp: /^(\d{1,2}).(\d{1,2}).(\d{4})$/
                          },
                          placeholder: 'dd/MM/yyyy'
                        }}
                        placeholder="DD/MM/YYYY"
                        value={formData.dateOfPurchase}
                      />
                      <Input
                        id="originalPriceOfGlasses"
                        labelText="Hvad var prisen oprindeligt på brillen? *"
                        name="originalPriceOfGlasses"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue(
                            'originalPriceOfGlasses',
                            event.target.value
                          );
                          setFormData({
                            ...formData,
                            originalPriceOfGlasses: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(
                            touched.originalPriceOfGlasses &&
                            errors.originalPriceOfGlasses
                          )
                        }
                        errorText={errors.originalPriceOfGlasses}
                        value={formData.originalPriceOfGlasses}
                      />
                      <Input
                        id="priceEstimate"
                        labelText="Anslå prisen for at anskaffe en tilsvarende brille i dag *"
                        name="priceEstimate"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('priceEstimate', event.target.value);
                          setFormData({
                            ...formData,
                            priceEstimate: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(touched.priceEstimate && errors.priceEstimate)
                        }
                        errorText={errors.priceEstimate}
                        value={formData.priceEstimate}
                      />
                      <Checkbox
                        id="consent-id"
                        label="Jeg erklærer herved, at foranstående oplysninger er i overensstemmelse med de faktiske forhold, og at jeg vil yde selskabet enhver assistance i forbindelse med skaden. *"
                        name="consent"
                        type="checkbox"
                        value="consent"
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('consent', event.target.checked);
                          setFormData({
                            ...formData,
                            consent: event.target.checked
                          });
                        }}
                        onBlur={handleBlur}
                        className={utils['pb-2']}
                        topAlignInput
                        checked={formData.consent}
                      />
                      {touched.consent && errors.consent && (
                        <>
                          <br />
                          <CustomError errorText={errors.consent} />
                        </>
                      )}
                      <Button variant={'primary'} type="submit" flexible>
                        Send
                      </Button>
                    </Col>
                  </Row>
                </form>
              );
            }}
          </Formik>
        </>
      )}
      {apiError && (
        <Alert
          alertType="error"
          showIcon
          text="Der skete en fejl ved indsendelse. Prøv venligst igen senere"
          title="Der skete en fejl."
          className={utils['mb-3']}
        />
      )}
    </>
  );
};

export default ClaimReportGlasses;
